import * as React from 'react';
import './_card-comments.scss';
import { ICardCommentsHintItem, ICardCommentsProps } from './types';
import { CLASS_COMMENTS_SECTION } from '../../constants';
import { ESettingsSectionType } from '../../../../../base/components/SettingsSection/types';
import { AsidePanelContext } from '../../../../asidePanel/components/AsidePanel/constants';
import { EAsidePanelProperty } from '../../../../asidePanel/components/AsidePanel/types';
import { CLASS_CARD_DETAILS_SECTION } from '../../../constants';
import { Tips } from '../../../../../base/components/Tips/Tips';
import {
    COMMENTS_ATTACHMENT_TIPS, COMMENTS_NO_YET,
    COMMENTS_SECTIONS_EMPTY_PLACEHOLDER_TITLE,
    COMMENTS_SECTIONS_PLACEHOLDER_HINTS
} from './constants';
import { Icon } from 'kui';
import { CommentsList } from '../../../../../base/components/Comments/components/CommentsList/CommentsList';
import { CommentInputHOC } from '../../../../../base/components/Comments/hocs/CommentsInputHOC/CommentsInputHOC';
import { TCommentId } from '../../../../../../../store/model/card/types/IComment';
import { COMMENT_ANIMATION_DURATION } from '../../../../../base/components/Comments/components/Comment/constants';
import {
    EScrollIntoViewPosition,
    scrollCommentIntoView
} from '../../../../../base/components/Comments/helpers/scrollCommentIntoView';
import { CommentHOC } from '../../../../../base/components/Comments/hocs/CommentHOC/CommentHOC';
import { CLASS_COMMENT } from '../../../../../base/components/Comments/constants';
import { CommentsHeaderHOC } from '../../hocs/CommentsHeaderHOC/CommentsHeaderHOC';

export function CommentsSection({
    assigneesCount,
    boardId,
    cardId,
    comments,
    commentCount,
    descOrder,
    isChatView,
    isDefaultProperty,
    isShow,
    isShowTips,
    sectionType,
    onEdit,
    onHideTips,
    onLoad,
    onResetUnseen,
}: ICardCommentsProps) {
    if (isChatView) return;

    const isReadonly = sectionType === ESettingsSectionType.READONLY;

    const className = CLASS_COMMENTS_SECTION;
    const panelClass = className + '__panel';
    const classEmpty = className + '__empty';
    const classText = classEmpty + '-text';

    const { openedProperty, addShowedProperty, showedProperties, isCommentsColumn } = React.useContext(AsidePanelContext);
    const [isShowSection, setShowSection] = React.useState(false);
    const [isLoading, setLoading] = React.useState(null);

    // убираем неподходящие варианты
    const filteredHints = COMMENTS_SECTIONS_PLACEHOLDER_HINTS.filter(item => !item.isAssignees || !!assigneesCount);
    const [hint] = React.useState(filteredHints[Math.floor(Math.random() * filteredHints.length)]);

    const onScrollToComment = (id: TCommentId) => {
        const comment = document.querySelector('.' + CLASS_COMMENT + '--' + id) as HTMLElement;
        if (comment) {
            scrollCommentIntoView(comment, EScrollIntoViewPosition.CENTER);
            comment.classList.add(CLASS_COMMENT + '--active');
            setTimeout(() => {
                comment.classList.remove(CLASS_COMMENT + '--active');
            }, COMMENT_ANIMATION_DURATION);
        }
    };

    React.useEffect(() => {
        if (isShow || isDefaultProperty) {
            setShowSection(true);
            addShowedProperty(EAsidePanelProperty.CARD_COMMENTS, isCommentsColumn);
        }
    }, [isShow, isDefaultProperty]);

    const ref = React.useRef(null);
    React.useEffect(() => {
        if (!comments.length || !isShowSection || !ref.current) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    const container = ref.current as HTMLElement;
                    if (!container) return;
                    if (container.offsetHeight < window.innerHeight * 0.2) { // когда вся секция меньше 20% экрана
                        onResetUnseen();
                        observer.unobserve(container);
                    } else {
                        const rect = container.getBoundingClientRect();
                        if ( // занимает 20% экрана или 40% секции видно
                            window.innerHeight - rect.top >= window.innerHeight * 0.2 ||
                            window.innerHeight - rect.top >= container.offsetHeight * 0.4
                        ) {
                            onResetUnseen();
                            observer.unobserve(container);
                        }
                    }
                }
            },
            {
                root: null,
                rootMargin: '0px',
                threshold: [0, 0.25, 0.5, 1],
            }
        );

        if (isCommentsColumn) {
            onResetUnseen();
        } else {
            const container = ref.current;
            observer.observe(container);
        }

        return () => {
            if (isCommentsColumn) return;
            const container = ref.current;
            if (container) {
                observer.unobserve(container);
            }
        };
    }, [isShowSection, comments.length, cardId]);

    if (
        !isShow && sectionType === ESettingsSectionType.READONLY ||
        !isShowSection && !showedProperties.has(EAsidePanelProperty.CARD_COMMENTS)
    ) {
        return;
    }

    const isEmpty = !comments.length;

    return (
        <div
            className={`
                ${CLASS_CARD_DETAILS_SECTION}
                ${CLASS_CARD_DETAILS_SECTION + '--comments'}
                ${className}
                ${descOrder ? className + '--desc' : ''}
                ${isReadonly ? className + '--readonly' : ''}
                ${isCommentsColumn ? className + '--column-view' : ''}
                ${isEmpty ? className + '--empty' : ''}
            `}
            tabIndex={-1}
            ref={ref}
        >
            <CommentsHeaderHOC cardId={cardId} />
            <div className={className + '__panel'}>
                {isCommentsColumn && isEmpty &&
                    <div className={classEmpty}>
                        <Icon xlink={'e-comments'} size={96} />
                        <span className={classEmpty + '-title'}>{COMMENTS_SECTIONS_EMPTY_PLACEHOLDER_TITLE}</span>
                        <div className={classEmpty + '-desc'}>
                            {hint.items.map((({ getText, Icon }: ICardCommentsHintItem) => {
                                const text = getText(assigneesCount);
                                return <span
                                    key={text}
                                    className={`${classText} ${Icon ? classText + '--w-icon' : ''}`}
                                >
                                    {Icon && <Icon />}
                                    {text}
                                </span>
                            }))}
                        </div>
                    </div>
                }
                {isCommentsColumn ?
                    <CommentsList
                        cardId={cardId}
                        boardId={boardId}
                        comments={comments}
                        commentCount={commentCount}
                        sectionType={sectionType}
                        ascOrder={!descOrder}
                        isReadonly={isReadonly}
                        onEdit={onEdit}
                        openedProperty={openedProperty}
                        onLoad={onLoad}
                    />
                    : <>
                        {/*{commentCount > comments.length &&*/}
                        {/*    <div className={className + '__more'}>*/}
                        {/*        {isLoading ?*/}
                        {/*            <Loader/>*/}
                        {/*            : <Button*/}
                        {/*                variant={'text'}*/}
                        {/*                onClick={() => {*/}
                        {/*                    setLoading(true);*/}
                        {/*                    onLoad().finally(() => {*/}
                        {/*                        setLoading(false);*/}
                        {/*                    });*/}
                        {/*                }}*/}
                        {/*            >*/}
                        {/*                {COMMENTS_SECTIONS_LOAD_MORE}*/}
                        {/*            </Button>*/}
                        {/*        }*/}
                        {/*    </div>*/}
                        {/*}*/}
                        <div className={`
                                ${className}__list
                                ${isEmpty ? className + '__list--no-comments' : ''}
                            `}>
                            {isEmpty &&
                                <span className={`
                                    ${classEmpty}-title
                                    ${classEmpty}-title--one-column
                                `}>{COMMENTS_NO_YET}</span>
                            }
                            {comments.map(comment => (
                                <CommentHOC
                                    key={comment.id}
                                    boardId={boardId}
                                    comment={comment}
                                    scrollToComment={onScrollToComment}
                                    onEdit={onEdit}
                                />
                            ))}
                        </div>
                        <CommentInputHOC
                            boardId={boardId}
                            cardId={cardId}
                            onScrollToComment={onScrollToComment}
                            openedProperty={openedProperty}
                            sectionType={sectionType}
                        />
                    </>
                }
            </div>
            {isShowTips && <Tips tips={COMMENTS_ATTACHMENT_TIPS} onHide={onHideTips}/>}
        </div>
    );
}
